import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Shield, Heart, Briefcase, Building2, Home, Gavel, ArrowRight, Quote, Calendar, Phone, Mail, MapPin, Star, Users, Award, CheckCircle } from 'lucide-react';

const iconMap = { Shield, Heart, Briefcase, Building2, Home, Gavel };
const fadeUp = { hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0 } };
const stagger = { hidden: {}, visible: { transition: { staggerChildren: 0.1 } } };

const LAWYER_DESK = "https://customer-assets.emergentagent.com/job_ozturk-law-pro/artifacts/jcz2vrun_WhatsApp%20Image%202026-07-02%20at%2015.12.41.jpeg";

export default function HomePage() {
  const { t, language } = useLanguage();
  const [services, setServices] = useState([]);
  const [contactForm, setContactForm] = useState({ name: '', email: '', phone: '', subject: '', message: '' });

  useEffect(() => {
    api.get('/services').then(r => setServices(r.data.slice(0, 6))).catch(() => {});
  }, []);

  const handleContact = async (e) => {
    e.preventDefault();
    toast.success(t('contact.success'));
    setContactForm({ name: '', email: '', phone: '', subject: '', message: '' });
  };

  const stats = [
    { value: "100+", label: t('stats.cases'), icon: CheckCircle },
    { value: "100+", label: t('stats.clients'), icon: Users },
    { value: "3+", label: t('stats.experience'), icon: Award },
    { value: "%95", label: t('stats.success_rate'), icon: Star },
  ];

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative min-h-[90vh] flex items-center" data-testid="hero-section">
        <div className="absolute inset-0 z-0">
          <img src="https://static.prod-images.emergentagent.com/jobs/ad3571aa-535d-4877-87ce-7c663392b9f4/images/1886f19422d276975a55ef4c2e2faad33b5c09374af013e3692cdf7b3691f9f7.png" alt="Hero" className="w-full h-full object-cover" />
          <div className="hero-overlay absolute inset-0" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32">
          <motion.div initial="hidden" animate="visible" variants={stagger} className="max-w-2xl">
            <motion.div variants={fadeUp} className="mb-4">
              <span className="text-[#C5A059] text-xs sm:text-sm uppercase tracking-[0.3em] font-medium">
                {language === 'tr' ? 'Hukuk Bürosu' : 'Law Office'}
              </span>
            </motion.div>
            <motion.h1 variants={fadeUp} className="text-5xl sm:text-6xl lg:text-7xl font-bold text-[#F3F4F6] leading-none mb-6" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              {t('hero.title')}
            </motion.h1>
            <motion.p variants={fadeUp} className="text-[#A1A1AA] text-base sm:text-lg leading-relaxed mb-10 max-w-lg">
              {t('hero.subtitle')}
            </motion.p>
            <motion.div variants={fadeUp} className="flex flex-wrap gap-4">
              <Link to="/contact">
                <Button className="bg-[#C5A059] text-[#0A0A0A] hover:bg-[#D4AF6A] px-8 py-3 text-sm font-semibold tracking-wide rounded-sm h-auto" data-testid="hero-appointment-btn">
                  <Phone className="h-4 w-4 mr-2" /> {t('hero.cta_contact')}
                </Button>
              </Link>
              <Link to="/services">
                <Button variant="outline" className="border-white/20 text-[#F3F4F6] hover:bg-white/5 hover:text-[#C5A059] hover:border-[#C5A059]/30 px-8 py-3 text-sm tracking-wide rounded-sm h-auto" data-testid="hero-contact-btn">
                  <ArrowRight className="h-4 w-4 mr-2" /> {t('services.title')}
                </Button>
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ABOUT PREVIEW - Hero'nun hemen altında */}
      <section className="py-24 bg-[#0A0A0A]" data-testid="about-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
              <div className="relative">
                <img src={LAWYER_DESK} alt="Av. Samet Öztürk" className="w-full rounded-sm object-cover aspect-[4/5]" />
                <div className="absolute -bottom-6 -right-6 bg-[#C5A059] text-[#0A0A0A] px-6 py-4 rounded-sm">
                  <span className="text-3xl font-bold" style={{ fontFamily: 'Cormorant Garamond, serif' }}>3+</span>
                  <span className="block text-xs uppercase tracking-wider font-medium">{t('about.experience')}</span>
                </div>
              </div>
            </motion.div>
            <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
              <span className="text-[#C5A059] text-xs uppercase tracking-[0.3em] font-medium">{t('about.title')}</span>
              <h2 className="text-3xl sm:text-4xl font-bold text-[#F3F4F6] mt-3 mb-6" style={{ fontFamily: 'Cormorant Garamond, serif' }}>{t('about.subtitle')}</h2>
              <p className="text-[#A1A1AA] leading-relaxed mb-8">{t('about.bio')}</p>
              <Link to="/about">
                <Button variant="outline" className="border-[#C5A059]/30 text-[#C5A059] hover:bg-[#C5A059]/10 hover:text-[#D4AF6A] rounded-sm" data-testid="about-more-btn">
                  {t('services.detail')} <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      <div className="section-divider" />

      {/* SERVICES */}
      <section className="py-24 bg-[#0A0A0A]" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial="hidden" animate="visible" variants={stagger} className="text-center mb-16">
            <motion.span variants={fadeUp} className="text-[#C5A059] text-xs uppercase tracking-[0.3em] font-medium">{t('services.title')}</motion.span>
            <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl font-bold text-[#F3F4F6] mt-3" style={{ fontFamily: 'Cormorant Garamond, serif' }}>{t('services.title')}</motion.h2>
            <motion.p variants={fadeUp} className="text-[#A1A1AA] mt-3 text-sm sm:text-base max-w-xl mx-auto">{t('services.subtitle')}</motion.p>
          </motion.div>
          <motion.div initial="hidden" animate={services.length > 0 ? "visible" : "hidden"} variants={stagger} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((svc, i) => {
              const Icon = iconMap[svc.icon] || Shield;
              return (
                <motion.div key={svc.id || i} variants={fadeUp}>
                  <Link to="/services" data-testid={`service-card-${i}`} className="block p-8 bg-[#121212] border border-white/10 rounded-sm card-hover group">
                    <div className="w-12 h-12 flex items-center justify-center bg-[#C5A059]/10 rounded-sm mb-5 group-hover:bg-[#C5A059]/20 transition-colors">
                      <Icon className="h-6 w-6 text-[#C5A059]" />
                    </div>
                    <h3 className="text-xl font-semibold text-[#F3F4F6] mb-3" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                      {language === 'tr' ? svc.title_tr : svc.title_en}
                    </h3>
                    <p className="text-[#A1A1AA] text-sm leading-relaxed mb-4">{language === 'tr' ? svc.description_tr : svc.description_en}</p>
                    <span className="text-[#C5A059] text-sm font-medium flex items-center gap-1 group-hover:gap-2 transition-all">
                      {t('services.detail')} <ArrowRight className="h-4 w-4" />
                    </span>
                  </Link>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* STATS */}
      <section className="py-20 bg-[#121212]" data-testid="stats-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.1 }} variants={stagger} className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, i) => (
              <motion.div key={i} variants={fadeUp} className="text-center" data-testid={`stat-${i}`}>
                <stat.icon className="h-8 w-8 text-[#C5A059] mx-auto mb-3" />
                <div className="text-4xl sm:text-5xl font-bold text-[#F3F4F6] mb-1" style={{ fontFamily: 'Cormorant Garamond, serif' }}>{stat.value}</div>
                <div className="text-[#A1A1AA] text-sm uppercase tracking-wider">{stat.label}</div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-24 bg-[#0A0A0A]" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.1 }} variants={stagger} className="text-center mb-16">
            <motion.span variants={fadeUp} className="text-[#C5A059] text-xs uppercase tracking-[0.3em] font-medium">{t('testimonials.title')}</motion.span>
            <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl font-bold text-[#F3F4F6] mt-3" style={{ fontFamily: 'Cormorant Garamond, serif' }}>{t('testimonials.title')}</motion.h2>
          </motion.div>
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.1 }} variants={stagger} className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {t('testimonials.items').map((item, i) => (
              <motion.div key={i} variants={fadeUp} className="p-8 bg-[#121212] border border-white/10 rounded-sm" data-testid={`testimonial-${i}`}>
                <Quote className="h-8 w-8 text-[#C5A059]/30 mb-4" />
                <p className="text-[#A1A1AA] text-sm leading-relaxed mb-6 italic">"{item.text}"</p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#C5A059]/20 rounded-full flex items-center justify-center">
                    <span className="text-[#C5A059] text-sm font-semibold">{item.name[0]}</span>
                  </div>
                  <div>
                    <div className="text-[#F3F4F6] text-sm font-medium">{item.name}</div>
                    <div className="text-[#52525B] text-xs">{item.role}</div>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      <div className="section-divider" />

      {/* CONTACT */}
      <section className="py-24 bg-[#0A0A0A]" data-testid="contact-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
              <span className="text-[#C5A059] text-xs uppercase tracking-[0.3em] font-medium">{t('contact.title')}</span>
              <h2 className="text-3xl sm:text-4xl font-bold text-[#F3F4F6] mt-3 mb-8" style={{ fontFamily: 'Cormorant Garamond, serif' }}>{t('contact.subtitle')}</h2>
              <div className="space-y-6">
                {[
                  { icon: MapPin, label: language === 'tr' ? 'Adres' : 'Address', value: t('contact.address'), href: null },
                  { icon: Phone, label: language === 'tr' ? 'Telefon' : 'Phone', value: t('contact.phone_number'), href: 'tel:+905063859352' },
                  { icon: Mail, label: 'E-posta', value: t('contact.email_address'), href: 'mailto:av.sametozturk1@gmail.com' },
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-[#C5A059]/10 rounded-sm flex items-center justify-center flex-shrink-0"><item.icon className="h-5 w-5 text-[#C5A059]" /></div>
                    <div>
                      <h4 className="text-[#F3F4F6] text-sm font-medium mb-1">{item.label}</h4>
                      {item.href ? <a href={item.href} className="text-[#A1A1AA] text-sm hover:text-[#C5A059] transition-colors">{item.value}</a> : <p className="text-[#A1A1AA] text-sm">{item.value}</p>}
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
            <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
              <form onSubmit={handleContact} className="space-y-4" data-testid="contact-form">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Input placeholder={t('contact.name')} value={contactForm.name} onChange={e => setContactForm({ ...contactForm, name: e.target.value })} className="bg-[#121212] border-white/10 text-[#F3F4F6] placeholder:text-[#52525B] rounded-sm focus:border-[#C5A059]" required data-testid="contact-name" />
                  <Input placeholder={t('contact.email')} type="email" value={contactForm.email} onChange={e => setContactForm({ ...contactForm, email: e.target.value })} className="bg-[#121212] border-white/10 text-[#F3F4F6] placeholder:text-[#52525B] rounded-sm focus:border-[#C5A059]" required data-testid="contact-email" />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Input placeholder={t('contact.phone')} value={contactForm.phone} onChange={e => setContactForm({ ...contactForm, phone: e.target.value })} className="bg-[#121212] border-white/10 text-[#F3F4F6] placeholder:text-[#52525B] rounded-sm focus:border-[#C5A059]" data-testid="contact-phone" />
                  <Input placeholder={t('contact.subject')} value={contactForm.subject} onChange={e => setContactForm({ ...contactForm, subject: e.target.value })} className="bg-[#121212] border-white/10 text-[#F3F4F6] placeholder:text-[#52525B] rounded-sm focus:border-[#C5A059]" required data-testid="contact-subject" />
                </div>
                <Textarea placeholder={t('contact.message')} value={contactForm.message} onChange={e => setContactForm({ ...contactForm, message: e.target.value })} className="bg-[#121212] border-white/10 text-[#F3F4F6] placeholder:text-[#52525B] rounded-sm focus:border-[#C5A059] min-h-[120px]" required data-testid="contact-message" />
                <Button type="submit" className="bg-[#C5A059] text-[#0A0A0A] hover:bg-[#D4AF6A] w-full rounded-sm font-medium tracking-wide" data-testid="contact-submit">{t('contact.send')}</Button>
              </form>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}
